import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FiExternalLink, FiEye, FiEyeOff, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SiteFooter from '../components/SiteFooter';
import SiteHeader from '../components/SiteHeader';
import { useLabels } from '../contexts/LanguageContext';
import { storeToken } from '../utils/cookie';
import { setUserInfo } from '../utils/localStore.util';
import { usePageMeta } from '../utils/pageMeta.util';

interface HipaaNotice {
  id: number;
  title: string;
  short_description: string;
  checkbox_label: string;
  privacy_link: string;
  contact_email: string;
  features: string[];
}

interface HipaaModalProps {
  notice: HipaaNotice;
  onContinue: () => void;
  onClose: () => void;
}

function HipaaModal({ notice, onContinue, onClose }: HipaaModalProps) {
  const [checked, setChecked] = useState(false);

  const handleContinue = () => {
    if (!checked) {
      toast.error('Please check the box to agree to the Notice of Privacy Practices.');
      return;
    }
    onContinue();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl px-6 pt-6 pb-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close"
        >
          <FiX size={20} />
        </button>

        <h2 className="text-xl font-bold text-slate-900 mb-4 text-center">{notice.title}</h2>

        <p className="text-sm text-slate-600 mb-3">{notice.short_description}</p>

        <ul className="mb-5 space-y-1">
          {notice.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
              <span className="mt-1 w-2 h-2 rounded-full bg-brand-700 flex-shrink-0" />
              {f}
            </li>
          ))}
        </ul>

        <label className="flex items-start gap-3 cursor-pointer mb-5">
          <input
            type="checkbox"
            className="sr-only"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span
            className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors ${checked ? 'bg-brand-700 border-brand-700' : 'border-slate-400 bg-white'
              }`}
          >
            {checked && (
              <svg
                className="w-3 h-3 text-white"
                fill="none"
                viewBox="0 0 12 12"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path d="M2 6l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <span className="text-sm text-slate-700">{notice.checkbox_label}</span>
        </label>

        <a
          href={notice.privacy_link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-brand-700 underline mb-6 hover:text-brand-500 transition-colors break-all"
        >
          {notice.privacy_link}
          <FiExternalLink size={12} className="flex-shrink-0" />
        </a>

        <button
          onClick={handleContinue}
          className="w-full py-3 rounded-full bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold transition-colors duration-200"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

export default function LoginNew() {
  const labels = useLabels();

  usePageMeta({
    title: 'Login | HCAE Client Portal',
    description:
      'Sign in to the HCAE Client Portal to access your documents, schedule, billing, downloads, and support.',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [hipaaNotice, setHipaaNotice] = useState<HipaaNotice | null>(null);
  const [showHipaaModal, setShowHipaaModal] = useState(false);

  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}hipaa-privacy-notice/list`)
      .then((res) => {
        if (res.data?.status === true && res.data?.data) {
          setHipaaNotice(res.data.data);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch HIPAA notice:', err);
      });
  }, [API_BASE_URL]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (hipaaNotice) {
      setShowHipaaModal(true);
      return;
    }

    await performLogin();
  };

  const handleHipaaContinue = async () => {
    setShowHipaaModal(false);
    await performLogin();
  };

  const performLogin = async () => {
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await axios.post(`${API_BASE_URL}login`, formData);
      const resData = response?.data;

      if (resData?.status === true) {
        setUserInfo(resData?.data);
        storeToken(resData?.data?.api_token);
        toast.success(resData?.message || labels.lbl_login_success_msg);
        navigate('/dashboard');
      } else {
        toast.error(resData?.message || 'Login failed');
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || 'Something went wrong';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex flex-col bg-white text-black font-montserrat">
        <SiteHeader />

        <section className="flex-1 mx-auto w-full max-w-5xl px-5 py-12 lg:px-8 lg:py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Left — client portal copy */}
            <div>
              <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-ihc-plum">
                Client Portal
              </span>

              <h1 className="mt-5 text-[34px] font-bold leading-[1.12] tracking-tight text-ihc-ink sm:text-[44px]">
                Welcome Back.
                <br />
                Log In To Continue.
              </h1>

              <p className="mt-4 font-semibold uppercase tracking-[0.22em] text-ihc-muted">
                OR
              </p>

              <p className="mb-4 font-semibold uppercase tracking-[0.22em] text-ihc-muted">
                Download App
              </p>

              <div className="mt-3 flex items-center gap-2 sm:gap-4">
                <a
                  href="https://apps.apple.com/us/app/home-care-admin-experts/id6738555607"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src="/apple.svg" alt="Download on the App Store" className="h-12 sm:h-14 w-auto" />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.hcae.app"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src="/play.png" alt="Get it on Google Play" className="h-12 sm:h-14 w-auto" />
                </a>
              </div>
            </div>

            <div className="lg:pl-4">
              <div
                className="w-full max-w-md mx-auto backdrop-blur-xl p-8 rounded-3xl border border-ihc-plum/20 bg-cover bg-center bg-no-repeat shadow-xl"
                style={{ backgroundImage: 'url(/assets/bg_desktop.png)' }}
              >
                {/* <div className="flex justify-center">
                  <img src="/assets/logo.png" className="h-60 w-fit object-cover" alt="logo" />
                </div> */}
                <div className="max-w-sm mx-auto">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label
                        htmlFor="username-new"
                        className="block text-sm font-semibold text-black mb-1"
                      >
                        {labels.lbl_username}
                      </label>
                      <input
                        id="username-new"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder={`Enter ${labels.lbl_username}`}
                        autoComplete="username"
                        className="w-full px-4 py-3 bg-white/90 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-ihc-plum/40 focus:border-transparent placeholder-slate-400 text-slate-800 shadow-sm"
                        required
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="password-new"
                        className="block text-sm font-semibold text-black mb-1"
                      >
                        Password
                      </label>
                      <div className="relative">
                        <input
                          id="password-new"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Password"
                          autoComplete="current-password"
                          className="w-full px-4 py-3 pr-12 bg-white/90 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-ihc-plum/40 focus:border-transparent placeholder-slate-400 text-slate-800 shadow-sm"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-ihc-plum hover:text-ihc-ink focus:outline-none"
                        >
                          {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className={`w-full py-3 px-4 mt-2 rounded-full text-sm font-medium transition-colors duration-200
                        ${loading
                          ? 'bg-brand-400 cursor-not-allowed'
                          : 'bg-brand-700 hover:bg-brand-800 text-white'
                        }`}
                    >
                      {loading ? 'Logging in...' : labels.lbl_login_txt}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <SiteFooter />
      </div>

      {showHipaaModal && hipaaNotice && (
        <HipaaModal
          notice={hipaaNotice}
          onContinue={handleHipaaContinue}
          onClose={() => setShowHipaaModal(false)}
        />
      )}
    </>
  );
}
